import { useCallback } from 'react';

import { TrackingHandler } from 'client/tracking/handler';
import { TrackingConstant } from 'client/tracking/constant';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import {
  isFieldValueValid,
  getValidationErrorMessage,
  validateConditionsQuestionsResponses,
  transformMileage,
  MILEAGE_INPUT,
} from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import {
  CONDITION_AND_MILEAGE,
  COLORS_AND_OPTIONS,
  PARTNER_OFFER_CONDITIONS,
  DRAWER_CREATIVE_ID,
  APPRAISAL_PAGE_CREATIVE_ID,
} from 'site-modules/shared/constants/appraisal/appraisal';
import { validateAndUpdateStep } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-step-processor';
import {
  isConditionAndMileageStepValid,
  isColorsAndOptionsStepValid,
  isPartnerOfferConditionsStepValid,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-validators';

const EVENT_DATA_USER_INPUT = {
  action_category: TrackingConstant.USER_ACTION_CATEGORY,
  action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
};

/**
 * Encapsulates all "user picks a value" handlers that validate and update step state.
 *
 * @param {Object} params
 * @param {Function} params.setState - state setter
 * @param {boolean} params.isExtendedReport - whether this is the extended partner-offer flow
 * @param {Object} params.stateRef - ref always pointing at the current state (for changeMileage no-op guard)
 */
export function useSelectionHandlers({ setState, isExtendedReport, stateRef }) {
  const selectCondition = useCallback(
    ({ target: { value } }) => {
      setState(prev => {
        const newState = { ...prev, condition: value };
        return validateAndUpdateStep(newState, CONDITION_AND_MILEAGE, isConditionAndMileageStepValid, isExtendedReport);
      });
    },
    [isExtendedReport, setState]
  );

  const selectOptions = useCallback(
    (options, optionsCount, isStepDone = true) => {
      setState(prev => {
        const newState = { ...prev, options, optionsCount };
        if (isStepDone) {
          return validateAndUpdateStep(newState, COLORS_AND_OPTIONS, isColorsAndOptionsStepValid, isExtendedReport);
        }
        return newState;
      });
    },
    [isExtendedReport, setState]
  );

  const selectExteriorColor = useCallback(
    (exteriorColor, isStepDone = true) => {
      setState(prev => {
        const newState = { ...prev, exteriorColor };
        if (isStepDone) {
          return validateAndUpdateStep(newState, COLORS_AND_OPTIONS, isColorsAndOptionsStepValid, isExtendedReport);
        }
        return newState;
      });
    },
    [isExtendedReport, setState]
  );

  const selectInteriorColor = useCallback(
    (interiorColor, isStepDone = true) => {
      setState(prev => {
        const newState = { ...prev, interiorColor };
        if (isStepDone) {
          return validateAndUpdateStep(newState, COLORS_AND_OPTIONS, isColorsAndOptionsStepValid, isExtendedReport);
        }
        return newState;
      });
    },
    [isExtendedReport, setState]
  );

  const changeMileage = useCallback(
    value => {
      if (value === stateRef.current.mileage) return; // skip delayHeavyTask entirely on no-op
      delayHeavyTask(() => {
        setState(prev => {
          // double-guard inside updater for concurrent-mode safety
          if (value === prev.mileage) return prev;
          const styleYear = prev.vehicle?.style?.year;
          const additionalParametersForValidation = { styleYear };
          const isMileageValid = isFieldValueValid(
            CONDITION_AND_MILEAGE,
            MILEAGE_INPUT,
            value,
            additionalParametersForValidation
          );
          const errorMap = new Map(prev.errorMap);
          if (isMileageValid) {
            errorMap.delete(MILEAGE_INPUT);
          } else {
            errorMap.set(
              MILEAGE_INPUT,
              getValidationErrorMessage(CONDITION_AND_MILEAGE, MILEAGE_INPUT, value, additionalParametersForValidation)
            );
          }

          const newState = { ...prev, mileage: value, isMileageValid, errorMap };
          return validateAndUpdateStep(
            newState,
            CONDITION_AND_MILEAGE,
            isConditionAndMileageStepValid,
            isExtendedReport
          );
        });
      });
    },
    [isExtendedReport, setState, stateRef]
  );

  const onMileageBlur = useCallback(() => {
    delayHeavyTask(() => {
      setState(prev => {
        const mileage = transformMileage(prev.mileage);
        const isMileageValid = isFieldValueValid(CONDITION_AND_MILEAGE, MILEAGE_INPUT, prev.mileage, {
          styleYear: prev.vehicle?.style?.year,
        });

        if (isMileageValid) {
          TrackingHandler.fireEvent('update_mileage', {
            event_data: {
              ...EVENT_DATA_USER_INPUT,
              creative_id: isExtendedReport ? APPRAISAL_PAGE_CREATIVE_ID : DRAWER_CREATIVE_ID,
              value: mileage,
            },
          });
        }

        return { ...prev, mileage, isMileageValid };
      });
    });
  }, [isExtendedReport, setState]);

  const updateQuestionnaireResponses = useCallback(
    (verboseResponse, questionsAnswered, hasConditionsQuestionsError = false) => {
      setState(prev => {
        const areQuestionnaireResponsesValid =
          !hasConditionsQuestionsError && validateConditionsQuestionsResponses(verboseResponse);

        const currentPartnerOfferConditionsStep = prev.steps.find(step => step.id === PARTNER_OFFER_CONDITIONS);
        const isQuestionnaireStateUnchanged =
          prev.partnerQuestionnaireVerboseResponse === verboseResponse &&
          prev.partnerQuestionnaireAnswers === questionsAnswered &&
          prev.areQuestionnaireResponsesValid === areQuestionnaireResponsesValid &&
          // Include step.isDone so we do not skip when step completion is stale.
          // Safe early-return only when stored step completion matches freshly computed validity.
          currentPartnerOfferConditionsStep?.isDone === areQuestionnaireResponsesValid;

        if (isQuestionnaireStateUnchanged) {
          return prev;
        }

        const newState = {
          ...prev,
          areQuestionnaireResponsesValid,
          partnerQuestionnaireVerboseResponse: verboseResponse,
          partnerQuestionnaireAnswers: questionsAnswered,
        };

        return validateAndUpdateStep(
          newState,
          PARTNER_OFFER_CONDITIONS,
          isPartnerOfferConditionsStepValid,
          isExtendedReport
        );
      });
    },
    [isExtendedReport, setState]
  );

  return {
    selectCondition,
    selectOptions,
    selectExteriorColor,
    selectInteriorColor,
    changeMileage,
    onMileageBlur,
    updateQuestionnaireResponses,
  };
}
